import React, { useEffect, useRef } from "react";
import { useStore } from "@/stores/useStore";
import { useToast } from "@/hooks/useToast";
import { useServiceAdvisors } from "@/hooks/shop/useServiceAdvisors";
import { useCustomerUpdate } from "@/hooks/useCustomerUpdate";
import { useToastSound } from "@/hooks/useToastSound";
import { useNotifications } from "@/hooks/useNotifications";
import { useSnoozeAllCustomerUpdates } from "@/hooks/useSnoozeAllCustomerUpdates";
import {
  CustomerUpdateUsageLocation,
  useCustomerUpdatesAnalytics,
} from "@/hooks/useCustomerUpdatesAnalytics";
import { useInboxPanel } from "@/hooks/useInboxPanel";
import { canClaimToastSlot, TOAST_PRIORITY } from "@/lib/toastEvictionCooldown";

export const MyCustomerUpdateToastManager: React.FC = () => {
  const myCustomerUpdates = useStore((state) => state.myCustomerUpdates);
  const myNextCustomerUpdate = useStore((state) => state.myNextCustomerUpdate);
  const isCustomerUpdatesTabVisible = useStore(
    (state) => state.isCustomerUpdatesTabVisible
  );
  const { isOpen: isInboxPanelOpen } = useInboxPanel();
  const { snoozeAllCustomerUpdates, snoozeAllCustomerUpdatesEnabled } =
    useSnoozeAllCustomerUpdates();
  const subscribeToMyCustomerUpdates = useStore(
    (state) => state.subscribeToMyCustomerUpdates
  );
  const unsubscribeFromMyCustomerUpdates = useStore(
    (state) => state.unsubscribeFromMyCustomerUpdates
  );
  const shop = useStore((state) => state.shop);
  const cards = useStore((state) => state.cards);
  const serviceAdvisors = useServiceAdvisors();
  const { playToastSound } = useToastSound();
  const { showCustomerUpdateNotification } = useNotifications();
  const { logSnoozeUsed, logSnoozeAllUsed, logCustomerUpdateCompleted } =
    useCustomerUpdatesAnalytics();

  const {
    handleSnoozeUpdate,
    handleSubmit: handleBaseSubmit,
    snoozeTimeMin,
  } = useCustomerUpdate(myNextCustomerUpdate?.id);

  const { showCustomerUpdate, toasts } = useToast();

  // Track what we've already handled in this session
  const handledIdsRef = useRef<Set<string>>(new Set());
  const notifiedIdsRef = useRef<Set<string>>(new Set());
  const currentToastRef = useRef<{
    id: string;
    dismiss: () => void;
  } | null>(null);
  const isShowingRef = useRef(false);

  useEffect(() => {
    subscribeToMyCustomerUpdates();
    return () => {
      unsubscribeFromMyCustomerUpdates();
      // Dismiss any active toast when component unmounts (leaving dashboard)
      if (currentToastRef.current) {
        currentToastRef.current.dismiss();
        currentToastRef.current = null;
        isShowingRef.current = false;
      }
    };
  }, [subscribeToMyCustomerUpdates, unsubscribeFromMyCustomerUpdates]);

  // Handle notifications and sounds separately from toast creation
  useEffect(() => {
    const update = myNextCustomerUpdate;

    if (!update || !shop) return;
    if (notifiedIdsRef.current.has(update.id)) return;

    const cardData = cards?.find((c) => c.id === update.cardId);
    if (!cardData) return;

    // Mark as notified immediately to prevent duplicates
    notifiedIdsRef.current.add(update.id);

    // Play the toast sound. Mute, effect choice, the one-per-second limit and
    // autoplay-policy failures are all handled inside useToastSound.
    playToastSound("customerUpdate");

    // Show browser notification if tab is not visible
    const customerName = update.customerDisplayName || "Customer";
    const carInfo = update.vehicle || "Vehicle";
    showCustomerUpdateNotification(customerName, carInfo);
  }, [
    myNextCustomerUpdate?.id,
    shop?.id,
    playToastSound,
    showCustomerUpdateNotification,
    myNextCustomerUpdate,
    shop,
    cards,
  ]);

  // Show toast only when we have a new customer update (not when navigating)
  useEffect(() => {
    const update = myNextCustomerUpdate;
    if (!update || !shop) return;
    if (handledIdsRef.current.has(update.id)) return;
    if (isShowingRef.current) return;
    if (
      !canClaimToastSlot({
        toasts,
        myPriority: TOAST_PRIORITY.CUSTOMER_UPDATE,
        myToastId: currentToastRef.current?.id ?? null,
      })
    ) {
      return;
    }

    // Hide toasts only when BOTH inbox panel is open AND customer updates tab is visible
    if (isInboxPanelOpen && isCustomerUpdatesTabVisible) return;

    const cardData = cards?.find((c) => c.id === update.cardId);
    if (!cardData || !handleSnoozeUpdate || !handleBaseSubmit) return;

    // Ensure any existing toast is closed
    if (currentToastRef.current) {
      currentToastRef.current.dismiss();
      currentToastRef.current = null;
    }

    isShowingRef.current = true;

    const toastResult = showCustomerUpdate({
      initialValues: {
        ...update,
      },
      location: CustomerUpdateUsageLocation.TOAST,
      cardData,
      shop,
      serviceAdvisors,
      snoozeTime: snoozeTimeMin,
      snoozeAllEnabled: snoozeAllCustomerUpdatesEnabled,
      onDismiss: () => {
        // Just dismiss the toast, do NOT mark as handled
        if (currentToastRef.current) {
          currentToastRef.current.dismiss();
          currentToastRef.current = null;
          isShowingRef.current = false;
        }
      },
      onSnoozeAll: async () => {
        try {
          logSnoozeAllUsed();
          await snoozeAllCustomerUpdates();
        } finally {
          // Dismiss toast after snooze all
          if (currentToastRef.current) {
            currentToastRef.current.dismiss();
            currentToastRef.current = null;
            isShowingRef.current = false;
          }
        }
      },
      onSubmit: async (values) => {
        try {
          logCustomerUpdateCompleted(
            CustomerUpdateUsageLocation.TOAST,
            values.userId !== update.userId,
            values.timeUntil
          );
          await handleBaseSubmit(values);
        } finally {
          // Mark as handled and dismiss toast after submission
          handledIdsRef.current.add(update.id);
          if (currentToastRef.current) {
            currentToastRef.current.dismiss();
            currentToastRef.current = null;
            isShowingRef.current = false;
          }
        }
      },
      onSnooze: async () => {
        try {
          logSnoozeUsed(CustomerUpdateUsageLocation.TOAST);
          await handleSnoozeUpdate();
        } finally {
          // Mark as handled and dismiss toast after snooze
          handledIdsRef.current.add(update.id);
          if (currentToastRef.current) {
            currentToastRef.current.dismiss();
            currentToastRef.current = null;
            isShowingRef.current = false;
          }
        }
      },
    });

    currentToastRef.current = {
      id: toastResult.id,
      dismiss: toastResult.dismiss,
    };
    // `toasts` is deliberately omitted: CU is top priority (TOAST_PRIORITY.CUSTOMER_UPDATE
    // is the max), so canClaimToastSlot only ever blocks CU if a bug elsewhere holds the
    // slot at the same priority -- CU never needs to wait on `toasts` changing to retry.
    // Watching it here only tied CU's post-dismiss retry timing to the ~1ms
    // TOAST_REMOVE_DELAY window, causing an instant reappear after a plain dismiss.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    myNextCustomerUpdate,
    shop,
    cards,
    handleSnoozeUpdate,
    handleBaseSubmit,
    serviceAdvisors,
    snoozeTimeMin,
    snoozeAllCustomerUpdatesEnabled,
    snoozeAllCustomerUpdates,
    showCustomerUpdate,
    myCustomerUpdates.length,
    logSnoozeAllUsed,
    logCustomerUpdateCompleted,
    logSnoozeUsed,
    isCustomerUpdatesTabVisible,
    isInboxPanelOpen,
  ]);

  // Dismiss any active toast when customer updates tab becomes visible AND panel is open
  useEffect(() => {
    if (
      isInboxPanelOpen &&
      isCustomerUpdatesTabVisible &&
      currentToastRef.current
    ) {
      currentToastRef.current.dismiss();
      currentToastRef.current = null;
      isShowingRef.current = false;
    }
  }, [isInboxPanelOpen, isCustomerUpdatesTabVisible]);

  // If the update disappears, dismiss the toast, but DO NOT clear handledIdsRef
  useEffect(() => {
    if (!myNextCustomerUpdate && currentToastRef.current) {
      currentToastRef.current.dismiss();
      currentToastRef.current = null;
      isShowingRef.current = false;
    }
  }, [myNextCustomerUpdate]);

  return null;
};
